import React from 'react';
import { Trans } from '@lingui/macro';
import { Link, Group, Box, spacing, Skeleton } from '@landr/maestro';
import getFilterParamsToType from 'apollo/userRecentSearch/helpers/getFilterParamsToType';
import { useApiFiltersContext } from 'contexts/ApiFiltersContext/ApiFiltersContext';
import { ApiFilterItem, ApiFilterList } from 'contexts/ApiFiltersContext/types';
import { FiltersParams } from 'types/params';
import { RoutePaths } from 'types/enums';
import { Genre, Instrument, Sfx, Tag, BaseMeta } from 'types';
import styled from 'styled-components';
import { useToLocalizedPath } from 'hooks/useToLocalizedPath/useToLocalizedPath';

const FiltersContainer = styled(Box)`
    column-count: 6;
    column-gap: ${spacing('xxl')};

    ${({ theme }) => theme.breakpoint('lg')`
        column-count: 5;
    `}

    ${({ theme }) => theme.breakpoint('ml')`
        column-count: 4;
    `}

    ${({ theme }) => theme.breakpoint('md')`
        column-count: 3;
    `}

    ${({ theme }) => theme.breakpoint('ms')`
        column-count: 2;
    `}

    ${({ theme }) => theme.breakpoint('sm')`
        column-count: 1;
    `}
`;

const FilterGroup = styled(Group)`
    gutter: ${spacing('xxs')};
    column-break-inside: avoid;
    margin-bottom: ${spacing('xxl')};
    overflow-wrap: break-word;
`;

const SubFilterList = ({
    path,
    filters,
}: {
    path: RoutePaths;
    filters: (Instrument | Genre | Sfx | Tag | BaseMeta)[];
}) => {
    const toLocalizedPath = useToLocalizedPath();

    return (
        <Group direction="vertical" gutter="xxs">
            {filters.map((filter) => (
                <Group.Item>
                    <Link
                        href={toLocalizedPath(`/${path}/${filter.code}`)}
                        contrast="low"
                        hasUnderline="hover"
                    >
                        <Trans id={filter.code} />
                    </Link>
                </Group.Item>
            ))}
        </Group>
    );
};

const isInstrumentFilters = (item: ApiFilterItem): item is Instrument => {
    return (item as Instrument).subInstruments !== undefined;
};

const isGenreFilters = (item: ApiFilterItem): item is Genre => {
    return (item as Genre).subGenres !== undefined;
};

const isSfxFilters = (item: ApiFilterItem): item is Sfx => {
    return (item as Sfx).subSfxs !== undefined;
};

const getPathAndFilters = (item: ApiFilterItem) => {
    if (isInstrumentFilters(item)) {
        return { path: RoutePaths.Instruments, filters: item.subInstruments };
    }

    if (isGenreFilters(item)) {
        return { path: RoutePaths.Genres, filters: item.subGenres };
    }

    if (isSfxFilters(item)) {
        return { path: RoutePaths.Sfxs, filters: item.subSfxs };
    }

    return { path: undefined, filters: [] };
};

const FilterList = ({ list }: { list: ApiFilterList }) => {
    const toLocalizedPath = useToLocalizedPath();

    return (
        <>
            {list.map((item) => {
                const { path, filters } = getPathAndFilters(item);

                if (!path) {
                    return null;
                }

                return (
                    <FilterGroup direction="vertical" gutter="xxs">
                        <Group.Item>
                            <Link
                                href={toLocalizedPath(`/${path}/${item.code}`)}
                                variant="secondary"
                                size="lg"
                                hasUnderline="hover"
                                fontWeight="bold"
                            >
                                <Trans id={item.code} />
                            </Link>
                        </Group.Item>
                        <Group.Item>
                            <SubFilterList path={path} filters={filters} />
                        </Group.Item>
                    </FilterGroup>
                );
            })}
        </>
    );
};

const SkeletonHeights = [
    200, 400, 250, 125, 350, 280, 125, 410, 300, 250, 210, 280,
];

const FilterSkeleton = () => (
    <>
        {SkeletonHeights.map((height) => (
            <FilterGroup direction="vertical" gutter="xs">
                <Group.Item>
                    <Skeleton width="100%" height="28px" />
                </Group.Item>
                <Group.Item>
                    <Skeleton width="100%" height={`${height}px`} />
                </Group.Item>
            </FilterGroup>
        ))}
    </>
);

export const ListFilters = ({
    filterParams,
}: {
    filterParams: FiltersParams;
}) => {
    const filterType = getFilterParamsToType(filterParams);
    const { apiFilters } = useApiFiltersContext();

    const apiFilter = apiFilters.find(
        (apiFilter) => apiFilter.name === filterType,
    );

    const showSkeleton = !apiFilter?.list;

    return (
        <FiltersContainer>
            {showSkeleton && <FilterSkeleton />}
            {!showSkeleton && <FilterList list={apiFilter.list} />}
        </FiltersContainer>
    );
};
